'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader2, Lock, PackageSearch, Search, X } from 'lucide-react';
import {
  CATEGORY_NOUN,
  CATEGORY_NOUN_ONE,
  CATEGORY_TAG,
  type CatalogCategory,
  type CatalogCounts,
  type CatalogEntry,
} from '@/lib/catalog/types';
import { CatalogPagination } from './catalog-nav';

/**
 * The catalog list and its search box.
 *
 * ONE control (Safras, 21 Sep 2026). The brand menu and the product-type menu
 * that sat under the box were removed: the box already searches brand and
 * product name together, so the menus were a second way to do what typing
 * does. A ?cat= link still renders its filtered view server-side, there is
 * simply nothing on the page that sets one.
 *
 * The first page of rows is handed in already rendered by the server, so the
 * product names are in the HTML for a crawler and for anyone with JavaScript
 * off. This component takes over once somebody types.
 *
 * Search is live rather than a page load. It debounces, calls /api/catalog and
 * swaps the rows in place, so a visitor sees the list narrow as they type
 * instead of watching the whole page reload per query. The URL is kept in step
 * with replaceState, which costs no round trip but leaves a link that can be
 * shared or reloaded, and the server renders the same view on that reload.
 *
 * Moving between pages is numbered pagination only (Safras, 2 Oct 2026): plain
 * links, rendered on the server, so a crawler can follow every one of them.
 * Load more and the A-Z bar were removed the same day. Load more was a button,
 * and while it was the only way past page one, pages 2 onwards had no link a
 * crawler could follow. A typed search gets page links too, to the
 * ?q=...&page=N view the server renders, so nothing past the first 120 results
 * needs a button to reach.
 */

export interface CatalogBrowserProps {
  /** Rows for the server-rendered page of the current view. */
  initialRows: CatalogEntry[];
  /** Matches for the current view. */
  initialTotal: number;
  initialQuery: string;
  initialCategory: CatalogCategory | null;
  page: number;
  totalPages: number;
}

interface CatalogResponse {
  rows: CatalogEntry[];
  total: number;
  counts: CatalogCounts;
  page: number;
  totalPages: number;
}

const DEBOUNCE_MS = 220;

/** The URL for a view. Kept in one place so the links and replaceState agree. */
function catalogHref(page: number, query: string, category: CatalogCategory | null): string {
  const params = new URLSearchParams();
  const q = query.trim();
  if (q) params.set('q', q);
  if (category) params.set('cat', category);
  if (page > 1) params.set('page', String(page));
  const qs = params.toString();
  return qs ? `/catalog?${qs}` : '/catalog';
}

export function CatalogBrowser({
  initialRows,
  initialTotal,
  initialQuery,
  initialCategory,
  page,
  totalPages,
}: CatalogBrowserProps) {
  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState<CatalogCategory | null>(initialCategory);
  const [rows, setRows] = useState(initialRows);
  const [total, setTotal] = useState(initialTotal);
  // The page of the current view on screen: the server's page, or 1 once a
  // typed search has replaced the rows.
  const [shownPage, setShownPage] = useState(page);
  const [pageCount, setPageCount] = useState(totalPages);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // What the server gave us, to restore when the box is cleared and the filter
  // is back where it started, rather than asking for the same page again.
  const server = useRef({
    rows: initialRows,
    total: initialTotal,
    page,
    totalPages,
  });
  // Rising id so a slow response for an earlier keystroke cannot overwrite the
  // results of a later one.
  const request = useRef(0);
  // Typing is debounced. The menus that set this aside for a tap are gone, so
  // every change to the box now comes from the keyboard.
  const lastQuery = useRef(initialQuery.trim());

  useEffect(() => {
    const q = query.trim();
    const typed = q !== lastQuery.current;
    lastQuery.current = q;

    if (q === initialQuery.trim() && category === initialCategory) {
      request.current += 1;
      const s = server.current;
      setRows(s.rows);
      setTotal(s.total);
      setShownPage(s.page);
      setPageCount(s.totalPages);
      setBusy(false);
      setFailed(false);
      return;
    }

    const id = ++request.current;
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(catalogEndpoint(1, q, category));
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as CatalogResponse;
        if (request.current !== id) return;
        setRows(data.rows);
        setTotal(data.total);
        setShownPage(1);
        setPageCount(data.totalPages);
        setFailed(false);
      } catch {
        if (request.current !== id) return;
        // Say the search failed. Showing an empty list would read as "we do not
        // stock that", which is a different and worse thing to tell a retailer.
        setFailed(true);
        setRows([]);
        setTotal(0);
        setShownPage(1);
        setPageCount(1);
      } finally {
        if (request.current === id) setBusy(false);
      }
    }, typed ? DEBOUNCE_MS : 0);

    return () => clearTimeout(timer);
  }, [query, category, initialQuery, initialCategory]);

  // Keep the address bar in step without a navigation, so the view is
  // shareable and survives a reload. Null state, never window.history.state:
  // handing the App Router back its own state object desynchronises the Router
  // Cache.
  useEffect(() => {
    const q = query.trim();
    const unchanged = q === initialQuery.trim() && category === initialCategory;
    const url = unchanged
      ? catalogHref(page, initialQuery, initialCategory)
      : catalogHref(1, q, category);
    if (window.location.pathname + window.location.search !== url) {
      window.history.replaceState(null, '', url);
    }
  }, [query, category, initialQuery, initialCategory, page]);

  const searching = query.trim() !== '';
  const noun = category
    ? total === 1
      ? CATEGORY_NOUN_ONE[category]
      : CATEGORY_NOUN[category]
    : `product${total === 1 ? '' : 's'}`;

  return (
    <>
      <div className="mt-8">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[var(--text-muted)]"
          />
          <input
            type="search"
            name="q"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the catalog by product name"
            // text-base on phones: anything smaller makes iOS Safari zoom the
            // page on focus, which on a list this long is disorienting.
            placeholder="Search by brand or product name"
            className="h-12 w-full rounded-full border border-[var(--border)] bg-[var(--surface)] pl-11 pr-14 text-base text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--accent)] md:text-sm"
          />
          <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center">
            {busy ? (
              <Loader2 className="size-4 animate-spin text-[var(--text-muted)]" aria-hidden="true" />
            ) : query ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="inline-flex size-8 items-center justify-center rounded-full text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
              >
                <X className="size-4" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <p className="mt-5 text-sm text-[var(--text-muted)]" aria-live="polite">
        {failed ? (
          <>The catalog is unavailable just now. Try again in a moment.</>
        ) : total === 0 ? (
          <>No products match that search.</>
        ) : (
          <>
            {rows.length < total ? (
              <>
                Showing <span className="tabular">{rows.length.toLocaleString('en-US')}</span> of{' '}
              </>
            ) : null}
            <span className="tabular">{total.toLocaleString('en-US')}</span>{' '}
            {searching ? `matching ${noun}` : noun}
          </>
        )}
      </p>

      {total === 0 || failed ? (
        <div className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-8 text-center">
          <PackageSearch className="mx-auto size-6 text-[var(--text-muted)]" aria-hidden="true" />
          <p className="mt-3 text-sm text-[var(--text-secondary)]">
            {failed
              ? 'The list could not load. This is our side, not yours.'
              : `Nothing here matches "${query.trim()}". The catalog changes every week, so a line missing today may be in next week's list.`}
          </p>
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setCategory(null);
            }}
            className="font-display mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--accent)] hover:underline"
          >
            Browse the whole catalog
          </button>
        </div>
      ) : (
        <>
          <div
            className={`mt-5 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--background)] ${
              busy ? 'opacity-60' : ''
            }`}
          >
            {/* Same header bar as the signed-in products table: navy, white,
                uppercase. One table design across the site. */}
            <div className="font-display flex items-center justify-between gap-4 bg-[var(--text-secondary)] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-white">
              <span>Product</span>
              <span className="shrink-0">Price</span>
            </div>
            <ul>
              {rows.map((row, i) => (
                <li
                  key={row.name}
                  className={`flex items-center justify-between gap-3 border-t border-[var(--border)] px-4 py-2.5 transition-colors hover:bg-[var(--row-hover)] ${
                    i % 2 === 1 ? 'bg-[var(--row-zebra)]' : 'bg-[var(--background)]'
                  }`}
                >
                  <span className="min-w-0 break-words text-sm text-[var(--text-primary)]">
                    {row.name}
                    {row.category ? (
                      <span className="ml-1.5 inline-flex items-center rounded-sm border border-[var(--border-strong)]/30 px-[4px] align-middle text-[9px] font-semibold uppercase leading-[1.5] tracking-[0.05em] text-[var(--text-muted)]">
                        {CATEGORY_TAG[row.category]}
                      </span>
                    ) : null}
                  </span>
                  {/* A label, not a link. One per row was 120 links to /signup
                      on every page, which divided the link equity that should
                      reach the brand pages and said nothing after the first. */}
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--accent-soft)] px-3 py-1.5 text-xs font-semibold text-[var(--accent)]">
                    <Lock className="size-3" aria-hidden="true" />
                    <span className="sr-only sm:not-sr-only">Sign in for price</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Hidden only while a typed search is in flight, when the rows on
              screen and the box no longer agree. Otherwise the rows always
              match the box, so the links carry what is in it. */}
          {busy ? null : (
            <CatalogPagination
              page={shownPage}
              totalPages={pageCount}
              hrefFor={(p) => catalogHref(p, query, category)}
            />
          )}
        </>
      )}
    </>
  );
}

/** Where the browser asks for a page of the same view the server renders. */
function catalogEndpoint(page: number, query: string, category: CatalogCategory | null): string {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (category) params.set('cat', category);
  params.set('page', String(page));
  return `/api/catalog?${params.toString()}`;
}

